'use client';

// base
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

// styles
import { Column, Row } from 'styles';
import { StyleCheckingPassword } from './style';

// components
import { BasicModal, BasicLabel, TextButton, BasicPassword } from 'components';

// contexts
import { useAlert } from 'contexts';

// domains
import { ReqLogin, useChangePassword } from 'domains/Auth';

interface ChangePasswordProps {
  open: boolean;
  onClose: () => void;
  loginRequest: ReqLogin;
}

const passwordRegExp = {
  lowerCase: /[a-z]/,
  specialCharacter: /[!@#$%^&*(),.?":{}|<>]/,
  number: /[0-9]/,
  length: /.{8,}/,
  consecutive: /(.)\1{3,}/
};

type CheckingPasswordKey = keyof CheckingPassword;

interface PasswordMessageType {
  msg: string;
  type: CheckingPasswordKey;
}

const passwordMessage: PasswordMessageType[] = [
  {
    msg: 'Password must contain a lowercase letter',
    type: 'isLowerCase'
  },
  {
    msg: 'Password must contain a special character',
    type: 'isSpecialCharacter'
  },
  {
    msg: 'Password must contain a number',
    type: 'isNumber'
  },
  {
    msg: 'Password must contain at least 8 characters',
    type: 'isLength'
  },
  {
    msg: "Password Can't be included more than 4 consecutive identical characters",
    type: 'isConsecutive'
  },
  {
    msg: 'Password and Password Confirm are the same',
    type: 'isSame'
  }
];

interface CheckingPassword {
  isSpecialCharacter: boolean;
  isLowerCase: boolean;
  isNumber: boolean;
  isLength: boolean;
  isConsecutive: boolean;
  isSame: boolean;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ open, onClose, loginRequest }) => {
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [checkingPassword, setCheckingPassword] = useState<CheckingPassword>({
    isSpecialCharacter: false,
    isLowerCase: false,
    isNumber: false,
    isLength: false,
    isConsecutive: false,
    isSame: false
  });

  const { mutateAsync: changePassword } = useChangePassword();
  const { showAlert } = useAlert();
  const router = useRouter();

  useEffect(() => {
    const isLowerCase = passwordRegExp.lowerCase.test(newPassword as string);
    const isSpecialCharacter = passwordRegExp.specialCharacter.test(newPassword as string);
    const isNumber = passwordRegExp.number.test(newPassword as string);
    const isLength = newPassword?.length >= 8;
    const isConsecutive = newPassword && !/(.)\1{3,}/.test(newPassword as string);
    const isSame = newPassword && confirmPassword && newPassword === confirmPassword;

    setCheckingPassword({
      isLowerCase: !!isLowerCase,
      isSpecialCharacter: !!isSpecialCharacter,
      isNumber: !!isNumber,
      isLength: !!isLength,
      isConsecutive: !!isConsecutive,
      isSame: !!isSame
    });
  }, [newPassword, confirmPassword]);

  return (
    <BasicModal
      open={open}
      width={500}
      height={630}
      onClose={onClose}
      header="Change Password"
      body={
        <Column gap="30px">
          <Column gap="20px">
            <Column gap="12px">
              <BasicLabel label={'Initial password'} required />
              <BasicPassword
                placeholder="Initial password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
              />
            </Column>

            <Column gap="12px">
              <BasicLabel label={'New Password'} required />
              <Row style={{ position: 'relative', alignItems: 'center' }}>
                <BasicPassword
                  placeholder="Enter your password"
                  style={{ flex: 1 }}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Row>
            </Column>

            <Column gap="12px">
              <BasicLabel label={'check the new Password'} required />
              <BasicPassword
                placeholder="Re-Type password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Column>

            <Column gap="8px">
              {passwordMessage.map((item) => (
                <StyleCheckingPassword
                  key={item.type}
                  className={checkingPassword[item.type] ? 'true' : ''}
                >
                  {item.msg}
                </StyleCheckingPassword>
              ))}
            </Column>
          </Column>

          <Row style={{ justifyContent: 'end', gap: '10px' }}>
            <TextButton label={'Cancel'} variant="outlined" onClick={onClose} />
            <TextButton
              label={'Confirm'}
              onClick={() => {
                const failedValidation = Object.entries(checkingPassword).find(
                  ([_, value]) => value === false
                );

                if (failedValidation) {
                  const errorMessage = passwordMessage.find(
                    (msg) => msg.type === failedValidation[0]
                  )?.msg;

                  showAlert(`${errorMessage || 'Password validation failed'}`);
                  return;
                }

                changePassword(
                  {
                    data: {
                      mobile: loginRequest.mobile,
                      nationId: loginRequest.nationId,
                      tempPassword: tempPassword,
                      newPassword: newPassword
                    }
                  },
                  {
                    onSuccess: () => {
                      showAlert('Password changed successfully.', () => {
                        router.push('/dashboard');
                      });
                    },
                    onError: (error) => {
                      showAlert(`Error changing password: ${error.message}`);
                    }
                  }
                );
              }}
            />
          </Row>
        </Column>
      }
    />
  );
};
